import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import {
  fetchVendorEventAnalytics,
  type VendorEventAnalyticsPayload,
} from '../../../services/MasterAdmin/vendorService';
import type { SessionRangeFilter } from '../../../services/MasterAdmin/userService';
import type { UserAnalyticsSession, UserAnalyticsTimelineEvent } from '../../../services/MasterAdmin/userService';

const VENDOR_KIND_LABELS: Record<string, string> = {
  ambulance: 'Ambulance agency',
  hospital: 'Hospital',
  'medical-store': 'Medical store',
  'blood-bank': 'Blood bank',
  'doctor-clinic': 'Doctor / clinic',
  'diagnostic-center': 'Diagnostic center',
  product: 'Product partner',
};

const formatDateTime = (dateString: string | null | undefined) => {
  if (dateString == null || dateString === '') return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

type InnerTabId = 'sessions' | 'all';

const formatTrackedFrom = (platform?: string | null, trackedFrom?: string | null) => {
  if (trackedFrom) return trackedFrom;
  const p = (platform || '').toUpperCase();
  if (p === 'WEB') return 'Web';
  if (p === 'ANDROID' || p === 'IOS') return 'App';
  if (p) return p;
  return null;
};

const formatEventContext = (evt: UserAnalyticsTimelineEvent) => {
  const parts: string[] = [];
  const source = formatTrackedFrom(evt.platform, evt.trackedFrom);
  if (source) parts.push(source);
  if (evt.device) parts.push(evt.device);
  return parts.length > 0 ? parts.join(' · ') : null;
};

const formatSessionContext = (session: UserAnalyticsSession) => {
  const parts: string[] = [];
  const source = formatTrackedFrom(session.platform, session.trackedFrom);
  if (source) parts.push(source);
  if (session.device) parts.push(session.device);
  if (session.city) parts.push(session.city);
  return parts.length > 0 ? parts.join(' · ') : 'Device unknown';
};

export type VendorEventTimelinePanelProps = {
  vendorId: string;
  kindLabel?: string;
  /** When true (default for standalone page), show back button */
  showBack?: boolean;
  backPath?: string;
};

export const VendorEventTimelinePanel: React.FC<VendorEventTimelinePanelProps> = ({
  vendorId,
  kindLabel = 'Vendor',
  showBack = false,
  backPath = '/vendors',
}) => {
  const navigate = useNavigate();
  const [sessionRange, setSessionRange] = useState<SessionRangeFilter>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState<VendorEventAnalyticsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [tab, setTab] = useState<InnerTabId>('sessions');

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    setError(null);
    try {
      const params: { range: SessionRangeFilter; from?: string; to?: string } = {
        range: sessionRange,
      };
      if (sessionRange === 'custom' && customFrom && customTo) {
        params.from = customFrom;
        params.to = customTo;
      }
      const res = await fetchVendorEventAnalytics(vendorId, params);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError('Could not load event timeline');
      }
    } catch {
      setError('Failed to load vendor events. BigQuery may be unavailable or no events yet.');
    } finally {
      setLoading(false);
    }
  }, [vendorId, sessionRange, customFrom, customTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleSession = (id: string) => {
    setExpandedSessionId((prev) => (prev === id ? null : id));
  };

  const renderEventRow = (evt: UserAnalyticsTimelineEvent, idx: number, compact?: boolean) => {
    const target = evt.screenLabel ?? evt.entityId ?? null;
    const label = evt.eventTypeLabel ?? evt.eventType;
    const time = formatDateTime(evt.timestamp);
    const context = formatEventContext(evt);
    if (compact) {
      return (
        <li
          key={evt.eventId || `${idx}`}
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-gray-100 px-3 py-2 text-sm last:border-0 dark:border-gray-700/80"
        >
          <span className="shrink-0 text-xs tabular-nums text-gray-400">{time}</span>
          <span className="min-w-0 font-medium text-gray-900 dark:text-white">{label}</span>
          {target && (
            <span className="min-w-0 truncate text-gray-600 dark:text-gray-400">· {target}</span>
          )}
          {context && (
            <span className="w-full text-xs text-gray-500 dark:text-gray-400 sm:ml-auto sm:w-auto">
              {context}
            </span>
          )}
        </li>
      );
    }
    return (
      <li
        key={evt.eventId || `${idx}`}
        className="border-b border-gray-100 px-3 py-2 last:border-0 dark:border-gray-700/80"
      >
        <p className="text-sm text-gray-900 dark:text-white">
          <span className="font-medium">{label}</span>
          {target && <span className="font-normal text-gray-600 dark:text-gray-400"> · {target}</span>}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{time}</p>
        {context && <p className="text-xs text-gray-500 dark:text-gray-400">{context}</p>}
      </li>
    );
  };

  const tabBtn = (id: InnerTabId, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        tab === id
          ? 'bg-primary-600 text-white'
          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto w-full min-w-0 max-w-5xl px-0.5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {showBack ? (
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="inline-flex w-fit items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeftIcon className="h-4 w-4 shrink-0" />
            Back
          </button>
        ) : (
          <div />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={sessionRange}
            onChange={(e) => setSessionRange(e.target.value as SessionRangeFilter)}
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            aria-label="Date range"
          >
            <option value="day">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>
          {sessionRange === 'custom' && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
              <span className="text-gray-400">–</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </>
          )}
        </div>
      </div>

      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        {kindLabel}
        {data?.dateRange && (
          <>
            {' · '}
            {new Date(data.dateRange.from).toLocaleDateString()} –{' '}
            {new Date(data.dateRange.to).toLocaleDateString()}
          </>
        )}
      </p>

      <div className="mb-4 inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-600">
        {tabBtn('sessions', 'By session')}
        {tabBtn('all', 'All events')}
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary-600 dark:border-gray-600" />
        </div>
      )}

      {!loading && error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </p>
      )}

      {!loading && !error && data && (
        <>
          {tab === 'sessions' && (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
              {data.sessions.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No sessions in this range. Events appear when the vendor uses the mobile app or web panel.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.sessions.map((session: UserAnalyticsSession) => {
                    const open = expandedSessionId === session.sessionId;
                    return (
                      <li key={session.sessionId} className="bg-white dark:bg-gray-800/50">
                        <button
                          type="button"
                          onClick={() => toggleSession(session.sessionId)}
                          className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          <span className="text-gray-400">{open ? '▼' : '▶'}</span>
                          <span className="flex-1 font-medium text-gray-900 dark:text-white">
                            {formatDateTime(session.sessionStart)} · {session.eventsCount} event
                            {session.eventsCount !== 1 ? 's' : ''}
                          </span>
                          <span className="hidden truncate text-xs text-gray-500 sm:inline sm:max-w-[18rem] dark:text-gray-400">
                            {formatSessionContext(session)}
                          </span>
                        </button>
                        {open && (
                          <ul className="border-t border-gray-100 bg-gray-50/90 dark:border-gray-700 dark:bg-gray-900/40">
                            {(session.events || []).map((evt, idx) => renderEventRow(evt, idx, true))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {tab === 'all' && (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
              {data.timeline.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No events in range.
                </p>
              ) : (
                <>
                  <ul className="max-h-[min(70vh,32rem)] overflow-y-auto">
                    {data.timeline.slice(0, 200).map((evt, idx) => renderEventRow(evt, idx, true))}
                  </ul>
                  {data.timeline.length > 200 && (
                    <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-gray-700">
                      Showing 200 of {data.timeline.length}.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const VendorEventTimelinePage: React.FC = () => {
  const { vendorKind, vendorId } = useParams<{ vendorKind: string; vendorId: string }>();
  const backPath =
    vendorKind && vendorId ? `/vendors/${vendorKind}/${vendorId}` : '/vendors';
  const kindLabel =
    (vendorKind && VENDOR_KIND_LABELS[vendorKind]) ||
    (vendorKind ? vendorKind.replace(/-/g, ' ') : 'Vendor');

  if (!vendorId) {
    return <p className="text-sm text-gray-500">Missing vendor id.</p>;
  }

  return (
    <VendorEventTimelinePanel
      vendorId={vendorId}
      kindLabel={kindLabel}
      showBack
      backPath={backPath}
    />
  );
};

export default VendorEventTimelinePage;
